import { adaptToIso, adaptToDisplay } from 'adapters/v1/dates';
import { adaptJsonApiToDetail, adaptToJsonApiRequest } from 'adapters/v1/json_api';

import { cellAdapterFactory, cellLinkAdapterFactory } from 'adapters/v2/factories';

import { getCustomFieldPropNames } from 'lib/v1/customFields';

import { colorOptions, iconOptions } from 'components/v2/WorkflowControls/Autocomplete';

import { getUserDetails } from 'lib/v1/users';

import { titleizeSnake } from 'lib/v1/format';
import { getCustomFieldMapping } from 'lib/v1/customFields';

const dateFields = ['updated_at', 'created_at'];
const baseAttributes = [
  'name',
  'instructions',
  'active',
  'icon',
  'approval_flow_enable',
  'force_approval_order',
  'onboarding_flow_enable',
  'onboarding_fields',
];

// Helpers
const slugifyName = (str = '') => str.toLowerCase().trim().replace(/\s+/g, '_');
const ensureArray = (val) => (Array.isArray(val) ? val : []);

const adaptOnboardingFieldsToRequest = (fields = []) =>
  fields.map(({ description, display_name, field_type, options, required }) => ({
    name: slugifyName(display_name),
    label: titleizeSnake(display_name),
    type: field_type?.label,
    required: !!required,
    description,
    options: ensureArray(options)
      .map((opt) => (typeof opt === 'string' ? opt : opt.label))
      .filter(Boolean),
  }));

export const adaptOnboardingFieldsToForm = (fields = []) =>
  fields.map(({ description, label, name, options, required, type }) => {
    const mapping = { ...(getCustomFieldMapping() || {}) };
    // do not mutate original mapping object
    if (!mapping.fieldType) mapping.fieldType = {};
    mapping.fieldType.email = mapping.fieldType.email || 'Email';
    mapping.fieldType.file = mapping.fieldType.file || 'File';

    return {
      display_name: name,
      description,
      field_type: {
        label: type,
        title: mapping.fieldType?.[type],
      },
      required,
      options: ensureArray(options).map((option) => {
        const value = typeof option === 'string' ? option : option.label;
        return { label: value, title: value };
      }),
      allow_multiple: false,
    };
  });

export const adaptToRequest = (formData) => {
  const out = {};
  const relationships = {};
  const { reviewerEdit } = formData;

  const customFields = getCustomFieldPropNames('cardTemplates');

  // Instead of grabbing the entire object, just the items that might need sent
  [...customFields, ...baseAttributes].forEach((attr) => {
    if (formData[attr] === undefined) return;
    const hasLabel = formData[attr] && formData[attr].label !== undefined;
    out[attr] = hasLabel ? formData[attr].label : formData[attr];
  });

  if (formData.color?.color) out.color = formData.color.color;
  if (formData.icon?.icon) out.icon = formData.icon.icon;

  if (formData.statuses) {
    relationships.lanes = {
      data: formData.statuses.map(({ label: name, title: id }, i) => {
        // Filter out id from new statuses
        const originalId = `${id}`.includes('new') ? null : id;
        return { id: originalId, name, weight: i };
      }),
    };
  }

  // TODO: Fix after migrating
  if (typeof formData.attached_forms !== 'undefined') {
    const attached_forms = formData.attached_forms.map(({ label: formId, id, attachable_type }) => {
      return {
        resourceable_type: 'CardTemplate',
        resourceable_id: formData.id,
        attachable_id: formId,
        attachable_type: attachable_type || 'DigitalFormTemplate', // Use preserved type or default to new type
        id, // attachment id, null if being added
      };
    });

    const attached_documents = formData.attached_documents.map(({ label: docId, id }) => {
      return {
        resourceable_type: 'CardTemplate',
        resourceable_id: formData.id,
        attachable_id: docId,
        attachable_type: 'Document',
        id, // attachment id, null if being added
      };
    });

    relationships.attachments = { data: [...attached_forms, ...attached_documents] };
  }

  if (reviewerEdit !== undefined) {
    out.reviewer_id = reviewerEdit ? reviewerEdit.label : null;
    relationships.reviewers = { data: reviewerEdit ? [{ user_id: reviewerEdit.label }] : [] };
  }

  if (formData.onboarding_fields) {
    out.onboarding_fields = adaptOnboardingFieldsToRequest(formData.onboarding_fields);
  }

  return adaptToJsonApiRequest({
    attributes: adaptToIso(out, dateFields),
    relationships,
    type: 'card_template',
  });
};

export const adaptToDetail = (resource) => {
  if (!resource) return {};
  const obj = adaptToDisplay(adaptJsonApiToDetail(resource), dateFields);
  const { reviewers = [], approvers = [] } = obj;

  const reviewer = reviewers[0] || {};
  obj.reviewer_id = reviewer.id;
  obj.reviewer = reviewer.name;
  obj.reviewerEdit = { title: obj.reviewer, label: reviewer.id };

  const approver = approvers[0] || {};
  obj.approver_id = approver.id;
  obj.approver = approver.name;
  obj.approverEdit = { title: obj.approver, label: approver.user_id };
  obj.approversEdit = approvers.map(({ name, user_id }) => ({ title: name, label: user_id }));

  obj.activeStatus = obj.active === true ? 'Yes' : 'No';
  if (obj.deleted_at) obj.activeStatus = 'Archived';

  if (obj.attachments) {
    const documents = obj.attachments
      .filter((obj) => obj.attachable && obj.attachable_type === 'Document')
      .map((obj) => ({ id: obj.id, title: obj.attachable.name, label: obj.attachable_id }));

    // TODO: Fix after migrating
    const forms = obj.attachments
      .filter(
        (obj) =>
          obj.attachable &&
          ['DigitalFormTemplate', 'Company::Template'].includes(obj.attachable_type)
      )
      .map((obj) => ({
        id: obj.id,
        title: obj.attachable.title,
        label: obj.attachable_id,
        attachable_type: obj.attachable_type, // Preserve the original type
      }));

    obj.attached_forms = forms;
    obj.attached_documents = documents;
  }

  // not doing require anymore
  // obj.requireResponse = obj.attachments.some((a) => a.required);

  obj.statuses = (obj.lanes || []).map(({ id, name }) => ({
    title: id,
    label: name,
  }));

  if (obj.color) obj.color = colorOptions.find(({ color }) => color === obj.color);
  if (obj.icon) obj.icon = iconOptions.find(({ icon }) => icon === obj.icon);

  obj.reviewer_user = getUserDetails(obj.reviewer_id) || {};

  if (obj.onboarding_fields) {
    obj.onboarding_fields = adaptOnboardingFieldsToForm(obj.onboarding_fields);
  }

  return obj;
};

export const adaptToForm = (resource) => {
  if (!resource) return {};
  return { ...resource };
};

// table adapters
export const cellAdapter = cellAdapterFactory(adaptToDetail);
export const cellLinkAdapter = cellLinkAdapterFactory(adaptToDetail);
